"use client"

import * as React from "react"
import { usePage } from "@inertiajs/react"
import { BellRingIcon, ClipboardListIcon, LandmarkIcon, LayoutDashboardIcon, LayoutGridIcon, UsersIcon } from "lucide-react"

import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { TeamSwitcher } from "@/components/team-switcher"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar"
import { paths } from "@/utils/paths"
import { useTranslations } from "@/utils/translations"

type SharedProps = {
  current_user?: { id: number; email: string; role: string }
  current_organization?: { id: number; name: string }
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { current_user, current_organization } = usePage<SharedProps>().props
  const { t } = useTranslations()

  const navItems = [
    {
      title: t("nav.dashboard"),
      icon: LayoutDashboardIcon,
      url: paths.dashboard,
    },
    {
      title: t("nav.clients"),
      icon: UsersIcon,
      url: paths.clients,
    },
    {
      title: t("nav.service_orders"),
      icon: ClipboardListIcon,
      url: paths.serviceOrders,
    },
    {
      title: "Fluxo de Caixa",
      icon: LandmarkIcon,
      url: paths.ledgerEntries,
    },
    {
      title: t("nav.catalog"),
      icon: LayoutGridIcon,
      url: paths.products,
      isActive: true,
      items: [
        {
          title: t("nav.products"),
          url: paths.products
        },
        {
          title: t("nav.services"),
          url: paths.services
        },
        {
          title: t("nav.plans"),
          url: paths.plans
        },
      ],
    },
  ]

  const ownerNavItems = [
    {
      title: "Payment reminders",
      icon: BellRingIcon,
      url: paths.paymentReminders,
    },
  ]

  const teams = current_organization
    ? [{ name: current_organization.name, logo: <LayoutGridIcon />, plan: t("settings.team_switcher.owner") }]
    : []

  const user = current_user
    ? { name: current_user.email, email: current_user.email, avatar: "", role: current_user.role }
    : { name: "", email: "", avatar: "", role: "staff" }

  return (
    <Sidebar variant="inset" collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={teams} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={current_user?.role === "owner" ? [...navItems, ...ownerNavItems] : navItems} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
